import { useEffect, useState } from 'react'
import Link from 'next/link'
import Head from 'next/head'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import styles from './Breadcrumbs.module.scss'
import { Breadcrumb, BreadcrumbsProps } from './types'
import { generateBreadcrumbsStructuredData } from 'utils/structuredData'

const Breadcrumbs = ({ className, hasHome, title }: BreadcrumbsProps) => {
  // ROUTER
  const router = useRouter()
  const { locale: language } = router

  // Local state
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])

  /**
   * Generates breadcrumbs based on current path
   */
  useEffect(() => {
    // remove query from the path
    const pathWithoutQuery = router.asPath.split('?')[0]

    // convert the path to array of subPaths and filter the empty ones out
    const splittedPath = pathWithoutQuery.split('/').filter((path) => path)

    // convert the array of subPaths to an array of breadcrumb objects
    const crumbList = splittedPath.map((subPath, idx) => ({
      href: `/${language}/${splittedPath.slice(0, idx + 1).join('/')}`,
      title: subPath.replaceAll('-', ' ')
    }))

    setBreadcrumbs(hasHome ? [{ href: '/', title: 'Home' }, ...crumbList] : crumbList)
  }, [hasHome, language, router])

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          key="breadcrumb-jsonld"
          dangerouslySetInnerHTML={generateBreadcrumbsStructuredData(breadcrumbs)}
        />
      </Head>
      <div className={classNames(styles.breadcrumbs, className)}>
        {breadcrumbs.map((crumb, idx) => {
          // the last crumb does not have to be a link
          if (breadcrumbs.length === idx + 1) {
            return (
              <span key={`${crumb.title}-${idx}`} className={styles['current-page']}>
                {title || crumb.title}
              </span>
            )
          }

          return (
            <div className={styles.crumb} key={`${crumb.title}-${idx}`}>
              <Link href={crumb.href} className={styles.link}>
                {crumb.title}
              </Link>
              <span>/</span>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default Breadcrumbs
